import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  Users,
  Building2,
  TrendingUp,
  DollarSign,
  Settings,
  LogOut,
  Shield,
  BarChart3,
  UserCheck,
  Store,
  CreditCard,
  CheckCircle,
  XCircle,
  Clock,
  Menu
} from 'lucide-react';
import { collection, getDocs, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { AllBusiness, AllCustomerShopping, ApprovalBusiness, getApprovalBusiness } from '../../services/api';
import { db } from '../../firebase/config';
import toast from 'react-hot-toast';
import AdminMenu from './AdminMenu';

const AdminDashboard = () => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalBusinesses: 0,
    totalTransactions: 0,
    totalRevenue: 0,
    totalRewards: 0
  });
  const [users, setUsers] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [pointRequests, setPointRequests] = useState([]);
  const [selectedTable, setSelectedTable] = useState('business'); // default to business
  const [salesRecords, setSalesRecords] = useState([]);
  // Dashboard recent activity date filter
  const [recentFrom, setRecentFrom] = useState('');
  const [recentTo, setRecentTo] = useState('');
  // Selected table filters/pagination
  const [tableSearch, setTableSearch] = useState('');
  const [tableFrom, setTableFrom] = useState(''); // used for sales
  const [tableTo, setTableTo] = useState(''); // used for sales
  const [tablePage, setTablePage] = useState(1);
  const ITEMS_PER_PAGE = 10;
  // Sorting for inline tables
  const [tableSortBy, setTableSortBy] = useState('');
  const [tableSortDir, setTableSortDir] = useState('asc');
  // Date presets
  const [recentPreset, setRecentPreset] = useState('today'); // today | last_week | last_month | custom
  const [tablePreset, setTablePreset] = useState('today'); // for sales/rewards
  // Modals and selections
  const [showBusinessModal, setShowBusinessModal] = useState(false);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [showBuyPointsModal, setShowBuyPointsModal] = useState(false);
  const [buyPointsForm, setBuyPointsForm] = useState({ businessEmail: '', points: '' });
  const RATE_PER_POINT = 1; // adjust if your backend uses a different rate

  useEffect(() => {
    fetchAdminData();
  }, []);

  const fetchAdminData = async () => {
    try {
      // Prefer API: AllBusiness (users, businesses) and AllCustomerShopping (transactions/rewards)
      try {
        const businessResp = await AllBusiness({});
       
        const usersList = Array.isArray(businessResp.extractedData) ? businessResp.extractedData : (businessResp.extractedData?.records || []);
        let txAgg = {};
try {
            const txResp = await AllCustomerShopping({});
            const txList = Array.isArray(txResp.extractedData) ? txResp.extractedData : (txResp.extractedData?.records || []);
            txAgg = txList.reduce((acc, t) => {
              // Primary join key is email_id per requirement; normalize to lowercase
              const rawKey = t.email_id || t.email || t.businessEmail || t.business_email || t.vendorEmail || t.vendor_email || t.profile_id || t.profileId || null;
              const key = typeof rawKey === 'string' ? rawKey.trim().toLowerCase() : rawKey;
              if (!key) return acc;
              if (!acc[key]) acc[key] = { totalAmount: 0, pointBalanceSum: 0 };
              const amt = Number(t.amount ?? t.total ?? 0);
              const pb = Number(t.point_balance ?? t.pointBalance ?? 0);
              if (isFinite(amt)) acc[key].totalAmount += amt;
              if (isFinite(pb)) acc[key].pointBalanceSum += pb;
              return acc;
            }, {});
          } catch (e) {
            // If transaction API fails, leave txAgg empty and fall back to zeros
            txAgg = {};
          }

        // Normalize users
        const normUsers = usersList.map((u, idx) => ({
          id: u.profile_id || u.id || String(idx),
          email: u.email || u.email_id || '',
          role: u.role || u.registrationType || 'customer',
          displayName: u.firstName ? `${u.firstName} ${u.lastName || ''}`.trim() : (u.displayName || u.businessName || u.name || ''),
          totalPoints: u.totalPoints || u.points || u.amount || 0,
          amountSaved: u.amountSaved || 0,
          shopDetails: u.shopDetails || [],
          address: u.address || '',
          phone: u.phone || '',
          firstName: u.firstName || '',
          lastName: u.lastName || '',
          registrationType: u.registrationType || '',
        }));
        setUsers(normUsers);

        const businessUsers = normUsers.filter(u => u.role === 'business');
        const normBusinesses = businessUsers.map((u, idx) => ({
          id: u.id || String(idx),
          businessName: u.displayName || 'Business',
          email: u.email,
          availablePoints: (() => {
            const keyEmail = (u.email || u.email_id || '').toLowerCase();
            if (keyEmail && txAgg[keyEmail]) return txAgg[keyEmail].pointBalanceSum || 0;
            return 0;
          })(),
          totalPurchases: (() => {
            const keyEmail = (u.email || u.email_id || '').toLowerCase();
            if (keyEmail && txAgg[keyEmail]) return txAgg[keyEmail].totalAmount || 0;
            return 0;
          })(),
          totalIncome: u.totalIncome ?? 0,
          shopDetails: u.shopDetails || [],
          address: u.address || '',
          phone: u.phone || '',
          firstName: u.firstName || '',
          lastName: u.lastName || '',
          registrationType: u.registrationType || '',
        }));
        setBusinesses(normBusinesses);

        // AllCustomerShopping for sales and recent transactions
        const shoppingResp = await AllCustomerShopping({});
        const shopping = Array.isArray(shoppingResp.extractedData) ? shoppingResp.extractedData : (shoppingResp.extractedData?.records || []);
        // Normalize transactions
        const txns = shopping.map((t, idx) => ({
          id: t.id || t.txn_id || String(idx),
          displayName: t.shopName || t.name || t.merchant,
          email: t.email_id,
          amountSaved: t.amount,
          rewards: t.point_given,
          date: t.date || t.created_on || new Date().toISOString(),
          business: t.shopName || t.name || t.merchant,
          role: t.role,
          amount: Number(t.amount || t.total || 0),
          points: Number(t.point_given || t.earnedPoints || 0),
        }));
        setSalesRecords(txns);

        // Per requirement, total rewards from transactions (sum of points)
        const totalRewards = txns.reduce((sum, t) => sum + (t.points || 0), 0);
        setStats({
          totalUsers: normUsers.length,
          totalBusinesses: normBusinesses.length,
          totalTransactions: txns.length,
          totalRevenue: txns.reduce((s, t) => s + (t.amount || 0), 0),
          totalRewards,
        });
      } catch (apiErr) {
        // Fallback to Firestore mock data if API fails
        const usersSnapshot = await getDocs(collection(db, 'users'));
        const usersData = [];
        usersSnapshot.forEach((doc) => {
          const data = doc.data();
          usersData.push({
            id: doc.id,
            email: data.email,
            role: data.role || 'customer',
            displayName: data.displayName || '',
            totalPoints: data.totalPoints || Math.floor(Math.random() * 1000),
            amountSaved: data.amountSaved || Math.floor(Math.random() * 5000)
          });
        });
        setUsers(usersData);
        const businessUsers = usersData.filter(u => u.role === 'business');
        const businessesData = businessUsers.map(u => ({
          id: u.id,
          businessName: u.displayName || 'Business User',
          email: u.email,
          availablePoints: Math.floor(Math.random() * 5000),
          totalPurchases: Math.floor(Math.random() * 200),
          totalIncome: Math.floor(Math.random() * 100000)
        }));
        setBusinesses(businessesData);
        const totalRewards = usersData
          .filter(u => u.role === 'customer')
          .reduce((sum, u) => sum + (u.totalPoints || 0), 0);
        setStats({
          totalUsers: usersData.length,
          totalBusinesses: businessesData.length,
          totalTransactions: Math.floor(Math.random() * 1000) + 500,
          totalRevenue: Math.floor(Math.random() * 50000) + 25000,
          totalRewards: totalRewards || Math.floor(Math.random() * 50000) + 5000
        });
        setSalesRecords([
          { id: 's1', date: new Date().toISOString(), business: 'ABC Store', customer: 'John Doe', amount: 1250, points: 125 },
          { id: 's2', date: new Date(Date.now() - 3600_000).toISOString(), business: 'XYZ Restaurant', customer: 'Jane Smith', amount: 890, points: 89 },
          { id: 's3', date: new Date(Date.now() - 86400_000).toISOString(), business: 'Fashion Hub', customer: 'A. Kumar', amount: 2199, points: 220 },
          { id: 's4', date: new Date(Date.now() - 2*86400_000).toISOString(), business: 'Tech Mart', customer: 'R. Singh', amount: 1599, points: 160 },
          { id: 's5', date: new Date(Date.now() - 3*86400_000).toISOString(), business: 'Daily Needs', customer: 'S. Rao', amount: 499, points: 50 },
        ]);
      }

      // Load approvals from API instead of mock
      try {
        const respApprovals = await getApprovalBusiness({});
        const listApprovals = Array.isArray(respApprovals.extractedData) ? respApprovals.extractedData : (respApprovals.extractedData?.records || []);
        const approvals = listApprovals.map((it, idx) => ({
          id: it.id || it._id || String(idx),
          businessName: it.businessName || (it.firstName ? `${it.firstName} ${it.lastName || ''}`.trim() : (it.name || (it.businessEmail?.split?.('@')[0] || 'Business'))),
          businessEmail: it.businessEmail || it.email || '',
          points: Number(it.points ?? it.point ?? 0),
          amount: Number(it.amount ?? 0),
          status: it.status || 'pending',
          requestDate: it.requestDate || it.created_on || new Date().toISOString(),
        }));
        setPointRequests(approvals);
      } catch (e) {
        console.warn('Failed to load approvals, leaving empty.', e);
        setPointRequests([]);
      }

    } catch (error) {
      console.error('Error loading admin data:', error);
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  // Business details modal handlers
  const openBusinessModal = (b) => { setSelectedBusiness(b); setShowBusinessModal(true); };
  const closeBusinessModal = () => { setShowBusinessModal(false); setSelectedBusiness(null); };
  // Buy points submit (uses ApprovalBusiness API)
  const handleBuyPointsSubmit = async (e) => {
    e.preventDefault();
    const pts = Number(buyPointsForm.points || 0);
    // const email = (buyPointsForm.businessEmail || '').trim();
    if ( !pts) { toast.error('Enter the points'); return; }
    const amount = pts * RATE_PER_POINT;
    try {
      await ApprovalBusiness({
        // businessEmail: email,
        points: pts,
        amount,
        status: 'pending',
        requestDate: new Date().toISOString(),
      });
      toast.success(`Buy request for ${pts} points (₹${amount.toLocaleString()}) sent for `);
      // Refresh approvals list so Admin can see it immediately if on that view
      try {
        const resp = await getApprovalBusiness({});
        const list = Array.isArray(resp.extractedData) ? resp.extractedData : (resp.extractedData?.records || []);
        const approvals = list.map((it, idx) => ({
          id: it.id || it._id || String(idx),
          businessName: it.businessName || it.name || it.firstName ? `${it.firstName || ''} ${it.lastName || ''}`.trim() : (it.businessEmail?.split?.('@')[0] || 'Business'),
          businessEmail: it.businessEmail || it.email || '',
          points: Number(it.points ?? it.point ?? 0),
          amount: Number(it.amount ?? 0),
          status: it.status || 'pending',
          requestDate: it.requestDate || it.created_on || new Date().toISOString(),
        }));
        setPointRequests(approvals);
      } catch {}
    } catch (err) {
      console.error('ApprovalBusiness failed', err);
      toast.error('Failed to send buy points request');
    } finally {
      setShowBuyPointsModal(false);
      setBuyPointsForm({ businessEmail: '', points: '' });
    }
  };

  const handleUserRoleUpdate = async (userId, newRole) => {
    try {
      await updateDoc(doc(db, 'users', userId), { role: newRole });
      setUsers(users.map(user =>
        user.id === userId ? { ...user, role: newRole } : user
      ));
      toast.success(`User role updated to ${newRole}`);
    } catch (error) {
      if (error.code === 'unavailable' || error.message.includes('offline')) {
        // Update locally when offline
        setUsers(users.map(user =>
          user.id === userId ? { ...user, role: newRole } : user
        ));
        toast('User role updated locally (offline mode)', { icon: '⚠️' });
      } else {
        console.error('Error updating user role:', error);
        toast.error('Failed to update user role');
      }
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    try {
      await deleteDoc(doc(db, 'users', userId));
      setUsers(users.filter(user => user.id !== userId));
      toast.success('User deleted successfully');
    } catch (error) {
      if (error.code === 'unavailable' || error.message.includes('offline')) {
        // Remove locally when offline
        setUsers(users.filter(user => user.id !== userId));
        toast('User removed locally (offline mode)', { icon: '⚠️' });
      } else {
        console.error('Error deleting user:', error);
        toast.error('Failed to delete user');
      }
    }
  };

  const handlePointRequestAction = async (requestId, action) => {
    try {
      const req = pointRequests.find(r => r.id === requestId);
      if (!req) { toast.error('Request not found'); return; }
      // Update status via API
      await ApprovalBusiness({
        id: requestId,
        businessEmail: req.businessEmail,
        points: Number(req.points || 0),
        amount: Number(req.amount || 0),
        status: action, // 'approved' | 'rejected'
        processedDate: new Date().toISOString(),
      });

      // Refresh approvals from API
      const resp = await getApprovalBusiness({});
      const list = Array.isArray(resp.extractedData) ? resp.extractedData : (resp.extractedData?.records || []);
      const approvals = list.map((it, idx) => ({
        id: it.id || it._id || String(idx),
        businessName: it.businessName || (it.firstName ? `${it.firstName} ${it.lastName || ''}`.trim() : (it.name || (it.businessEmail?.split?.('@')[0] || 'Business'))),
        businessEmail: it.businessEmail || it.email || '',
        points: Number(it.points ?? it.point ?? 0),
        amount: Number(it.amount ?? 0),
        status: it.status || 'pending',
        requestDate: it.requestDate || it.created_on || new Date().toISOString(),
      }));
      setPointRequests(approvals);

      if (action === 'approved') {
        toast.success(`Approved ${req.points} points for ${req.businessName || req.businessEmail}`);
      } else if (action === 'rejected') {
        toast.success(`Rejected point request from ${req.businessName || req.businessEmail}`);
      } else {
        toast.success('Request updated');
      }
    } catch (error) {
      console.error('Failed to process point request', error);
      toast.error('Failed to process point request');
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      toast.success('Logged out successfully');
    } catch (error) {
      toast.error('Failed to logout');
    }
  };

  // Derived: recent filtered sales
  const applyPreset = (preset) => {
    const now = new Date();
    let from = null, to = null;
    if (preset === 'today') {
      from = new Date(now); from.setHours(0,0,0,0);
      to = new Date(now); to.setHours(23,59,59,999);
    } else if (preset === 'last_week') {
      to = new Date(now); to.setHours(23,59,59,999);
      from = new Date(now); from.setDate(from.getDate() - 7); from.setHours(0,0,0,0);
    } else if (preset === 'last_month') {
      to = new Date(now); to.setHours(23,59,59,999);
      from = new Date(now); from.setMonth(from.getMonth() - 1); from.setHours(0,0,0,0);
    }
    return { from, to };
  };

  const filteredRecentSales = React.useMemo(() => {
    let data = [...salesRecords];
    // presets first if not custom
    if (recentPreset !== 'custom') {
      const { from, to } = applyPreset(recentPreset);
      if (from) data = data.filter(r => new Date(r.date) >= from);
      if (to) data = data.filter(r => new Date(r.date) <= to);
    }
    if (recentFrom) {
      const from = new Date(recentFrom);
      data = data.filter((r) => new Date(r.date) >= from);
    }
    if (recentTo) {
      const to = new Date(recentTo);
      to.setHours(23,59,59,999);
      data = data.filter((r) => new Date(r.date) <= to);
    }
    return data;
  }, [salesRecords, recentFrom, recentTo]);

  // Derived: selected table data with filters and pagination
  const selectedTableData = React.useMemo(() => {
    if (!selectedTable) return { rows: [], total: 0 };
    let rows = [];
    if (selectedTable === 'business') {
      rows = businesses;
      if (tableSearch) {
        const q = tableSearch.toLowerCase();
        rows = rows.filter((b) => (b.businessName||'').toLowerCase().includes(q) || (b.email||'').toLowerCase().includes(q));
      }
      // sort
      if (tableSortBy) {
        const dir = tableSortDir === 'asc' ? 1 : -1;
        rows = [...rows].sort((a,b)=>{
          const av = a[tableSortBy]; const bv = b[tableSortBy];
          if (av == null && bv == null) return 0; if (av == null) return 1; if (bv == null) return -1;
          if (typeof av === 'string') return av.localeCompare(bv)*dir;
          return (Number(av) - Number(bv))*dir;
        });
      }
    } else if (selectedTable === 'rewards') {
      // Rewards table: only search by name/email/amount (no date preset/range)
      rows = salesRecords;
      if (tableSearch) {
        const q = tableSearch.toLowerCase();
        rows = rows.filter((r) => (r.displayName||'').toLowerCase().includes(q) || (r.email||'').toLowerCase().includes(q) || String(r.amount||'').includes(q));
      }
      // sort
      if (tableSortBy) {
        const dir = tableSortDir === 'asc' ? 1 : -1;
        rows = [...rows].sort((a,b)=>{
          const av = a[tableSortBy]; const bv = b[tableSortBy];
          if (av == null && bv == null) return 0; if (av == null) return 1; if (bv == null) return -1;
          if (typeof av === 'string') return String(av).localeCompare(String(bv))*dir;
          if (av instanceof Date) return (av - bv)*dir;
          return (Number(av) - Number(bv))*dir;
        });
      }
    } else if (selectedTable === 'sales') {
      rows = salesRecords;
      // Apply preset when not custom
      if (tablePreset !== 'custom') {
        const { from, to } = applyPreset(tablePreset);
        if (from) rows = rows.filter(r => new Date(r.date) >= from);
        if (to) rows = rows.filter(r => new Date(r.date) <= to);
      }
      // Apply custom date range
      if (tablePreset === 'custom') {
        if (tableFrom) { const from = new Date(tableFrom); rows = rows.filter(r => new Date(r.date) >= from); }
        if (tableTo) { const to = new Date(tableTo); to.setHours(23,59,59,999); rows = rows.filter(r => new Date(r.date) <= to); }
      }
      // Search by name/email/amount
      if (tableSearch) {
        const q = tableSearch.toLowerCase();
        rows = rows.filter((r) => (
          (r.business||r.displayName||'').toLowerCase().includes(q) ||
          (r.email||'').toLowerCase().includes(q) ||
          String(r.amount||'').toLowerCase().includes(q)
        ));
      }
      if (tableSortBy) {
        const dir = tableSortDir === 'asc' ? 1 : -1;
        rows = [...rows].sort((a,b)=>{
          const av = a[tableSortBy]; const bv = b[tableSortBy];
          if (av == null && bv == null) return 0; if (av == null) return 1; if (bv == null) return -1;
          if (typeof av === 'string') return String(av).localeCompare(String(bv))*dir;
          if (av instanceof Date) return (av - bv)*dir;
          return (Number(av) - Number(bv))*dir;
        });
      }
    }
    const total = rows.length;
    const start = (tablePage - 1) * ITEMS_PER_PAGE;
    const pageRows = rows.slice(start, start + ITEMS_PER_PAGE);
    return { rows: pageRows, total };
  }, [selectedTable, tableSearch, tableFrom, tableTo, tablePage, tablePreset, tableSortBy, tableSortDir, businesses, users, salesRecords]);

  const setSort = (key) => {
    if (tableSortBy === key) setTableSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    else { setTableSortBy(key); setTableSortDir('asc'); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header (styled like BusinessHeader) */}
      <header className="bg-gradient-to-r from-blue-600 to-purple-600 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-6">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">Welcome back,</h1>
                <p className="text-blue-100">{currentUser?.email || 'Admin User'}</p>
              </div>
            </div>

            {/* Dropdown Menu */}
            <AdminMenu onSelect={(action) => {
              switch(action){
                case 'business': navigate('/admin/business'); break;
                case 'users': navigate('/admin/users'); break;
                case 'approval': navigate('/admin/approval'); break;
                case 'sales_history': setSelectedTable('sales'); break;
                // case 'buy_points': setShowBuyPointsModal(true); break;
                case 'logout': handleLogout(); break;
                default: break;
              }
            }} />
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards (clickable) */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {/* Total Business Owners */}
          <button onClick={() => { setTableSearch(''); setTableFrom(''); setTableTo(''); setTablePage(1); setSelectedTable('business'); }} className="text-left bg-white rounded-lg shadow p-6 hover:shadow-md transition">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <Building2 className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Business Owners</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalBusinesses}</p>
              </div>
            </div>
          </button>

          {/* Total Reward */}
          <button onClick={() => { setTableSearch(''); setTableFrom(''); setTableTo(''); setTablePage(1); setSelectedTable('rewards'); }} className="text-left bg-white rounded-lg shadow p-6 hover:shadow-md transition">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <CreditCard className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Reward Points Given</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalRewards.toLocaleString()}</p>
              </div>
            </div>
          </button>

          {/* Total Sales */}
          <button onClick={() => { setTableSearch(''); setTableFrom(''); setTableTo(''); setTablePage(1); setSelectedTable('sales'); }} className="text-left bg-white rounded-lg shadow p-6 hover:shadow-md transition">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <TrendingUp className="h-6 w-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total (Txns)</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalTransactions}</p>
              </div>
            </div>
          </button>
        </div>

        {/* Conditional: show selected table OR recent activity */}
        {!selectedTable ? (
          <div className="bg-white rounded-lg shadow mb-8">
            <div className="px-6 py-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">Recent Transaction History</h3>
            </div>
            {/* Date filters */}
            <div className="px-6 pt-4 grid grid-cols-1 md:grid-cols-5 gap-4">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Preset</label>
                <select value={recentPreset} onChange={(e)=>{setRecentPreset(e.target.value); const p=e.target.value; if(p!=='custom'){ setRecentFrom(''); setRecentTo(''); }}} className="w-full border border-gray-300 rounded-lg px-3 py-2">
                  <option value="today">Today</option>
                  <option value="last_week">Last Week</option>
                  <option value="last_month">Last Month</option>
                  <option value="custom">Custom</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">From</label>
                <input type="date" value={recentFrom} onChange={(e)=>setRecentFrom(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">To</label>
                <input type="date" value={recentTo} onChange={(e)=>setRecentTo(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
              </div>
              <div className="flex items-end">
                <button onClick={()=>{setRecentFrom(''); setRecentTo('');}} className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg">Clear</button>
              </div>
            </div>
            <div className="p-6 overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Points Given</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredRecentSales.slice(0,5).map((s) => (
                    <tr key={s.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{new Date(s.date).toLocaleString()}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{s.business}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{s.role}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">₹{s.amount.toLocaleString()}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{s.points}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow p-4 mb-8">
            {/* Filters for selected table */}
            {selectedTable === 'business' && (
              <div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <input type="text" value={tableSearch} onChange={(e)=>{setTableSearch(e.target.value); setTablePage(1);}} placeholder="Search business or email..." className="border border-gray-300 rounded-lg px-3 py-2" />
                  <div className="flex items-end">
                    <button onClick={()=>{setTableSearch(''); setTablePage(1);}} className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg">Clear</button>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th onClick={()=>setSort('businessName')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">Business {tableSortBy==='businessName' ? (tableSortDir==='asc'?'▲':'▼') : ''}</th>
                        <th onClick={()=>setSort('email')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">Email {tableSortBy==='email' ? (tableSortDir==='asc'?'▲':'▼') : ''}</th>
                        <th onClick={()=>setSort('availablePoints')} className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">Available Points {tableSortBy==='availablePoints' ? (tableSortDir==='asc'?'▲':'▼') : ''}</th>
                        <th onClick={()=>setSort('totalPurchases')} className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">Total Purchases {tableSortBy==='totalPurchases' ? (tableSortDir==='asc'?'▲':'▼') : ''}</th>
                        {/* <th onClick={()=>setSort('totalIncome')} className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">Total Income {tableSortBy==='totalIncome' ? (tableSortDir==='asc'?'▲':'▼') : ''}</th> */}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {selectedTableData.rows.map((b) => (
                        <tr key={b.id} onClick={()=>openBusinessModal(b)} className="cursor-pointer hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{b.businessName}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{b.email}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{Number(b.availablePoints || 0).toLocaleString()}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{Number(b.totalPurchases || 0).toLocaleString()}</td>
                          {/* <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">₹{(b.totalIncome ?? 0).toLocaleString()}</td> */}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {selectedTable === 'rewards' && (
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Total Reward Points Given</h4>
                {/* Filters: search by name/email/amount */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <input
                    type="text"
                    value={tableSearch}
                    onChange={(e)=>{setTableSearch(e.target.value); setTablePage(1);}}
                    placeholder="Search name, email, or amount..."
                    className="border border-gray-300 rounded-lg px-3 py-2"
                  />
                  <div className="flex items-end">
                    <button onClick={()=>{setTableSearch(''); setTablePage(1);}} className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg">Clear</button>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th onClick={()=>setSort('name')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">Name {tableSortBy==='name' ? (tableSortDir==='asc'?'▲':'▼') : ''}</th>
                        <th onClick={()=>setSort('email')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">Email {tableSortBy==='email' ? (tableSortDir==='asc'?'▲':'▼') : ''}</th>
                        <th onClick={()=>setSort('amount')} className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">Amount {tableSortBy==='amount' ? (tableSortDir==='asc'?'▲':'▼') : ''}</th>
                        <th onClick={()=>setSort('points')} className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">Points Given {tableSortBy==='points' ? (tableSortDir==='asc'?'▲':'▼') : ''}</th>
                        <th onClick={()=>setSort('createdAt')} className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">Date {tableSortBy==='createdAt' ? (tableSortDir==='asc'?'▲':'▼') : ''}</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {selectedTableData.rows
                        .filter((r)=>{
                          const q = (tableSearch || '').toLowerCase();
                          if (!q) return true;
                          const amt = String(Number(r.amount ?? r.amountSaved ?? 0));
                          return (r.displayName || '').toLowerCase().includes(q)
                            || (r.email || '').toLowerCase().includes(q)
                            || amt.includes(q);
                        })
                        .map((r) => (
                        <tr key={r.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{r.displayName || 'N/A'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{r.email || '-'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">₹{Number(r.amount ?? r.amountSaved ?? 0).toLocaleString()}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{Number(r.points ?? r.rewards ?? 0)}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{new Date(r.date).toLocaleDateString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {/* Pagination */}
                <div className="flex items-center justify-between py-4 text-sm text-gray-700">
                  <div>Showing {(tablePage-1)*ITEMS_PER_PAGE+1} to {Math.min(tablePage*ITEMS_PER_PAGE, selectedTableData.total)} of {selectedTableData.total} results</div>
                  <div className="space-x-2">
                    <button onClick={()=>setTablePage(Math.max(1, tablePage-1))} disabled={tablePage===1} className={`px-3 py-2 rounded-lg border ${tablePage===1 ? 'text-gray-400 border-gray-300' : 'text-gray-700 border-gray-300 hover:bg-gray-50'}`}>Prev</button>
                    <button onClick={()=>setTablePage(tablePage + (tablePage*ITEMS_PER_PAGE < selectedTableData.total ? 1 : 0))} disabled={tablePage*ITEMS_PER_PAGE >= selectedTableData.total} className={`px-3 py-2 rounded-lg border ${tablePage*ITEMS_PER_PAGE >= selectedTableData.total ? 'text-gray-400 border-gray-300' : 'text-gray-700 border-gray-300 hover:bg-gray-50'}`}>Next</button>
                  </div>
                </div>
              </div>
            )}
            {selectedTable === 'sales' && (
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">{tablePreset==='today' ? 'Today transactions' : tablePreset==='last_week' ? 'Last week transactions' : tablePreset==='last_month' ? 'Last month transactions' : tablePreset==='custom' ? 'Custom transactions' : 'Transactions'}</h4>
                {/* Filters: search by name/email/amount and date presets */}
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
                  <input
                    type="text"
                    value={tableSearch}
                    onChange={(e)=>{setTableSearch(e.target.value); setTablePage(1);}}
                    placeholder="Search name, email, or amount..."
                    className="border border-gray-300 rounded-lg px-3 py-2"
                  />
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Preset</label>
                    <select value={tablePreset} onChange={(e)=>{setTablePreset(e.target.value); if(e.target.value !== 'custom'){ setTableFrom(''); setTableTo(''); } setTablePage(1);}} className="w-full border border-gray-300 rounded-lg px-3 py-2">
                      <option value="today">Today</option>
                      <option value="last_week">Last Week</option>
                      <option value="last_month">Last Month</option>
                      <option value="custom">Custom</option>
                    </select>
                  </div>
                  {tablePreset === 'custom' && (
                    <>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">From</label>
                        <input type="date" value={tableFrom} onChange={(e)=>{setTableFrom(e.target.value); setTablePage(1);}} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">To</label>
                        <input type="date" value={tableTo} onChange={(e)=>{setTableTo(e.target.value); setTablePage(1);}} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
                      </div>
                    </>
                  )}
                  <div className="flex items-end">
                    <button onClick={()=>{setTableSearch(''); setTableFrom(''); setTableTo(''); setTablePreset('today'); setTablePage(1);}} className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg">Clear</button>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th onClick={()=>setSort('date')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">Date {tableSortBy==='date' ? (tableSortDir==='asc'?'▲':'▼') : ''}</th>
                        <th onClick={()=>setSort('name')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">Name {tableSortBy==='name' ? (tableSortDir==='asc'?'▲':'▼') : ''}</th>
                        <th onClick={()=>setSort('role')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">Role {tableSortBy==='role' ? (tableSortDir==='asc'?'▲':'▼') : ''}</th>
                        <th onClick={()=>setSort('amount')} className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">Amount {tableSortBy==='amount' ? (tableSortDir==='asc'?'▲':'▼') : ''}</th>
                        <th onClick={()=>setSort('points')} className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">Points {tableSortBy==='points' ? (tableSortDir==='asc'?'▲':'▼') : ''}</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {(() => {
                        // Build date range from preset
                        let from = null, to = null;
                        const now = new Date();
                        if (tablePreset === 'today') {
                          from = new Date(now); from.setHours(0,0,0,0);
                          to = new Date(now); to.setHours(23,59,59,999);
                        } else if (tablePreset === 'last_week') {
                          to = new Date(now); to.setHours(23,59,59,999);
                          from = new Date(now); from.setDate(from.getDate() - 7); from.setHours(0,0,0,0);
                        } else if (tablePreset === 'last_month') {
                          to = new Date(now); to.setHours(23,59,59,999);
                          from = new Date(now); from.setMonth(from.getMonth() - 1); from.setHours(0,0,0,0);
                        } else if (tablePreset === 'custom' && tableFrom && tableTo) {
                          from = new Date(tableFrom); from.setHours(0,0,0,0);
                          to = new Date(tableTo); to.setHours(23,59,59,999);
                        }

                        // Apply date and search filters
                        const filteredRows = selectedTableData.rows.filter((s) => {
                          const d = new Date(s.date);
                          const inRange = (!from || d >= from) && (!to || d <= to);
                          if (!inRange) return false;
                          const q = (tableSearch || '').toLowerCase();
                          if (!q) return true;
                          const amt = String(Number(s.amount || 0));
                          return (s.business || '').toLowerCase().includes(q)
                            || (s.email || '').toLowerCase().includes(q)
                            || amt.includes(q);
                        });

                        // Summary label
                        const label = tablePreset === 'today'
                          ? 'Today transactions'
                          : tablePreset === 'last_week'
                            ? 'Last week transactions'
                            : tablePreset === 'last_month'
                              ? 'Last month transactions'
                              : tablePreset === 'custom'
                                ? 'Custom transactions'
                                : 'Transactions';

                        return (
                          <>
                            <tr>
                              <td colSpan={5} className="px-6 py-2 text-sm text-gray-600">{label}: {filteredRows.length}</td>
                            </tr>
                            {filteredRows.map((s) => (
                              <tr key={s.id}>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{new Date(s.date).toLocaleDateString()}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{s.business}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{s.role}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">₹{Number(s.amount || 0).toLocaleString()}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{Number(s.points || 0).toLocaleString()}</td>
                              </tr>
                            ))}
                          </>
                        );
                      })()}
                    </tbody>
                  </table>
                </div>
                {/* Pagination */}
                <div className="flex items-center justify-between py-4 text-sm text-gray-700">
                  <div>Showing {(tablePage-1)*ITEMS_PER_PAGE+1} to {Math.min(tablePage*ITEMS_PER_PAGE, selectedTableData.total)} of {selectedTableData.total} results</div>
                  <div className="space-x-2">
                    <button onClick={()=>setTablePage(Math.max(1, tablePage-1))} disabled={tablePage===1} className={`px-3 py-2 rounded-lg border ${tablePage===1 ? 'text-gray-400 border-gray-300' : 'text-gray-700 border-gray-300 hover:bg-gray-50'}`}>Prev</button>
                    <button onClick={()=>setTablePage(tablePage + (tablePage*ITEMS_PER_PAGE < selectedTableData.total ? 1 : 0))} disabled={tablePage*ITEMS_PER_PAGE >= selectedTableData.total} className={`px-3 py-2 rounded-lg border ${tablePage*ITEMS_PER_PAGE >= selectedTableData.total ? 'text-gray-400 border-gray-300' : 'text-gray-700 border-gray-300 hover:bg-gray-50'}`}>Next</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {showBuyPointsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Buy Points</h3>
              <button onClick={()=>setShowBuyPointsModal(false)} className="text-gray-500 hover:text-gray-700">✕</button>
            </div>
            <form onSubmit={handleBuyPointsSubmit} className="space-y-4">
             
              <div>
                <label className="block text-sm text-gray-600 mb-1">Points</label>
                <input type="number" min="1" value={buyPointsForm.points} onChange={(e)=>setBuyPointsForm(f=>({...f, points: e.target.value}))} className="w-full border border-gray-300 rounded-lg px-3 py-2" placeholder="e.g. 1000" required />
                <p className="text-xs text-gray-500 mt-1">Amount: ₹{Number(buyPointsForm.points || 0) * RATE_PER_POINT}</p>
              </div>
              <div className="flex justify-end space-x-2">
                <button type="button" onClick={()=>setShowBuyPointsModal(false)} className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50">Cancel</button>
                <button type="submit" className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700">Submit</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;